import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { bannerShows, latestShows, hotShows, categories, shows } from '@/mocks/shows';

function Breadcrumb({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 py-3">
      <ol className="flex items-center flex-wrap gap-1 text-xs text-foreground-500">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && (
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            )}
            {item.href ? (
              <Link to={item.href} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground-700">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const banners = bannerShows;

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + banners.length) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const show = banners[current];

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden" aria-label="精选推荐">
      {banners.map((b, i) => (
        <div
          key={b.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={b.cover}
            alt={`${b.title} - 天狼影院热播推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-12">
            <div className="flex-1 max-w-xl">
              <div className="flex flex-wrap gap-2 mb-3">
                {show.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/30 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-500/20 text-accent-300 border border-accent-500/30 whitespace-nowrap">
                  {show.rating} 分
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mb-3 leading-tight">
                {show.title}
              </h1>
              <p className="text-sm md:text-base text-background-200/80 mb-4 line-clamp-2 leading-relaxed">
                {show.description}
              </p>
              <div className="flex items-center gap-3 text-xs md:text-sm text-background-300 mb-5 flex-wrap">
                <span><span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className="ri-user-line"></i></span>{show.director}</span>
                <span className="text-background-400">|</span>
                <span>{show.region}</span>
                <span className="text-background-400">|</span>
                <span>{show.year}</span>
                <span className="text-background-400">|</span>
                <span>{show.updateInfo}</span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/detail/${show.id}`}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-play-fill"></i></span>
                  立即观看
                </Link>
                <Link
                  to={`/detail/${show.id}`}
                  className="px-6 py-2.5 rounded-full bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors duration-200 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  查看详情
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors duration-200 backdrop-blur-sm cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors duration-200 backdrop-blur-sm cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>

      <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {banners.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'w-6 bg-primary-500' : 'w-1.5 bg-background-50/40 hover:bg-background-50/60'
            }`}
            aria-label={`切换到第${i + 1}张`}
          ></button>
        ))}
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="w-full px-4 md:px-6 -mt-10 relative z-10" aria-label="影视分类">
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-6">
        <div className="grid grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex flex-col items-center gap-2 p-3 md:p-4 rounded-lg hover:bg-background-100 transition-colors duration-200 group cursor-pointer"
            >
              <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                <i className={`${cat.icon} text-lg md:text-xl`}></i>
              </span>
              <span className="text-xs md:text-sm font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
              <span className="text-[10px] md:text-xs text-foreground-500">{cat.count}部</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ title, moreLink }: { title: string; moreLink: string }) {
  return (
    <div className="flex items-center justify-between mb-5 md:mb-6">
      <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">{title}</h2>
      <Link
        to={moreLink}
        className="text-xs md:text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1"
      >
        查看更多
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
      </Link>
    </div>
  );
}

function LatestSection() {
  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="最新更新">
      <SectionHeader title="最新更新" moreLink="/category/tv-series" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
        {latestShows.map((show) => (
          <VideoCard key={show.id} show={show} />
        ))}
      </div>
    </section>
  );
}

function HotSection() {
  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="热门推荐">
      <SectionHeader title="热门推荐" moreLink="/category/movies" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {hotShows.map((show) => (
          <div key={show.id} className="group flex gap-4 bg-background-50 rounded-lg p-3 hover:bg-background-200/40 transition-colors duration-200 cursor-pointer">
            <Link to={`/detail/${show.id}`} className="shrink-0">
              <div className="w-20 h-28 md:w-24 md:h-32 rounded-md overflow-hidden bg-background-200">
                <img
                  src={show.cover}
                  alt={`${show.title}`}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </Link>
            <div className="flex flex-col justify-between min-w-0 flex-1">
              <div>
                <Link to={`/detail/${show.id}`} className="block">
                  <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                    {show.title}
                  </h3>
                </Link>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 whitespace-nowrap">
                    {show.category}
                  </span>
                  <span className="text-[10px] font-semibold text-accent-500 whitespace-nowrap">
                    {show.rating}分
                  </span>
                </div>
                <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                  {show.description}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-foreground-500">{show.updateInfo}</span>
                {show.status !== '已完结' && show.status !== '已上映' && (
                  <span className="text-xs text-accent-500 font-medium">{show.status}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AllShowsSection() {
  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="全部剧集">
      <SectionHeader title="精彩剧集" moreLink="/category/tv-series" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
        {shows.map((show) => (
          <VideoCard key={show.id} show={show} />
        ))}
      </div>
    </section>
  );
}

function FeaturesBanner() {
  const features = [
    { icon: 'ri-hd-line', title: '高清画质', desc: '1080P超清体验' },
    { icon: 'ri-speed-up-line', title: '流畅不缓冲', desc: 'CDN极速加载' },
    { icon: 'ri-refresh-line', title: '每日更新', desc: '海量新剧上线' },
    { icon: 'ri-smartphone-line', title: '多端观看', desc: '手机电脑同步' },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="平台优势">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {features.map((f) => (
          <div key={f.title} className="flex flex-col items-center text-center p-5 rounded-xl bg-background-50 border border-background-200/60">
            <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
              <i className={`${f.icon} text-lg md:text-xl`}></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-800 mb-1">{f.title}</h3>
            <p className="text-xs text-foreground-500">{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <HeroBanner />
        <CategoryNav />
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '天狼影院 - 高清免费在线追剧' }]} />
        <LatestSection />
        <FeaturesBanner />
        <HotSection />
        <AllShowsSection />
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '天狼影院',
            url: 'https://tianlangcinema.com',
            description: '天狼影院提供电视剧全集连播，最新剧集高清免费在线观看，流畅不缓冲，海量剧集每日更新，满足追剧需求。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://tianlangcinema.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '天狼影院',
            url: 'https://tianlangcinema.com',
            description: '天狼影院 - 电视剧全集连播，最新剧集高清免费在线观看',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'contact@tianlangcinema.com',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '天狼影院 - 首页',
            description: '天狼影院提供电视剧全集连播，最新剧集高清免费在线观看，流畅不缓冲，海量剧集每日更新。',
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://tianlangcinema.com/',
                },
              ],
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '天狼影院是否需要注册才能观看？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '天狼影院无需注册即可免费在线观看所有电视剧和电影，打开网站即可流畅追剧。',
                },
              },
              {
                '@type': 'Question',
                name: '天狼影院的画质如何？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '天狼影院提供1080P高清画质，采用CDN加速技术确保流畅不缓冲的观影体验。',
                },
              },
              {
                '@type': 'Question',
                name: '天狼影院更新频率是怎样的？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '天狼影院每日更新海量剧集，涵盖电视剧、电影、动漫、综艺、纪录片等多种类型，满足不同观众的追剧需求。',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}